import { X } from 'lucide-react';

export interface PhotoMetaRow {
  readonly label: string;
  readonly value: string;
}

/**
 * Side drawer with the photo's details (L-1212). Shows ONLY the rows the host actually has plus the photo's pixel size once it has
 * loaded: a field a source does not carry is absent, never invented. Values render as React text (never HTML).
 */
export function PhotoMetaDrawer({
  rows,
  pixels,
  onClose,
}: {
  rows: readonly PhotoMetaRow[];
  pixels?: { w: number; h: number } | null;
  onClose: () => void;
}) {
  const all: PhotoMetaRow[] = [...rows];
  if (pixels && pixels.w > 0 && pixels.h > 0) all.push({ label: 'Pixels', value: `${pixels.w} × ${pixels.h}` });
  return (
    <aside
      aria-label="Photo details"
      data-testid="photo-meta-drawer"
      data-photo-meta-drawer=""
      className="absolute right-0 top-0 bottom-0 z-20 w-72 max-w-[85vw] overflow-y-auto bg-black/85 text-white shadow-xl border-l border-white/10"
      onClick={(e) => e.stopPropagation()}
      // The drawer sits inside the photo area that owns swipe and pan: a touch on it is the drawer's own (scroll), never a swipe to the next photo.
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h2 className="text-sm font-semibold">Details</h2>
        <button onClick={onClose} aria-label="Close details" className="p-1 rounded-full hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
      </div>
      {all.length === 0 ? (
        <p className="px-4 py-3 text-sm text-white/60">No details are available for this photo.</p>
      ) : (
        <dl className="px-4 py-3 space-y-3 text-sm">
          {all.map((r) => (
            <div key={r.label}>
              <dt className="text-xs uppercase tracking-wide text-white/50">{r.label}</dt>
              <dd className="break-words">{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </aside>
  );
}
